import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import {getAuthInfo} from '../utils/authUtils';
import {defaultAlert, defaultConfirm} from "../utils/alert";
import {handleTokenResponse, upgradeToCoordinator} from '../api/authApi';

interface AuthInfo {
    memberId: string;
    memberCode: string;
    role: string;
    coordinatorCode: string;
}

const ProtectedRoute = () => {
    const navigate = useNavigate();
    const [auth, setAuth] = useState<AuthInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const authInfo = getAuthInfo();
        setAuth(authInfo);
        setIsLoading(false);

        if (!authInfo) {
            defaultConfirm(
                "회원전용 서비스입니다. \n로그인 후 이용해주세요.",
                "로그인",
                "취소",
                (isConfirmed) => {
                    if (isConfirmed) {
                        navigate('/login', {replace: true});
                    } else {
                        navigate('/', {replace: true});
                    }
                }
            );
        }
    }, [navigate]);

    if (isLoading) {
        return null;
    }

    if (!auth) {
        return null;
    }

    return <Outlet context={{auth}}/>;
};

export const CoordinatorProtectedRoute = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState<'loading' | 'ok' | 'deny'>('loading');

    useEffect(() => {
        const checkAndUpgrade = async () => {
            const auth = getAuthInfo();

            if (!auth) {
                navigate('/login', {replace: true});
                return;
            }

            // 이미 코디 role이면 바로 진입
            if (auth.role === 'ROLE_COORDINATOR') {
                setStatus('ok');
                return;
            }

            // ROLE_MEMBER인 경우 → 코디 승인 여부 확인 후 토큰 업그레이드 시도
            try {
                const upgradeRes = await upgradeToCoordinator();
                if (upgradeRes.data.code === '00') {
                    handleTokenResponse(upgradeRes.data); // localStorage 토큰 교체
                    setStatus('ok');
                } else {
                    defaultAlert("코디회원 전용 서비스입니다.");
                    setStatus('deny');
                }
            } catch {
                // 미승인(NOT_FOUND 등) → 알럿 후 홈으로
                defaultAlert("코디회원 전용 서비스입니다.");
                setStatus('deny');
            }
        };

        checkAndUpgrade();
    }, [navigate]);

    if (status === 'loading') return null;
    if (status === 'deny') return <Navigate to="/" replace/>;

    return <Outlet context={{auth: getAuthInfo()}}/>;
};

export const useAuthInfo = () => {
    return getAuthInfo();
};

interface PaymentAuthContextType {
    isPaymentAuthed: boolean;
    setPaymentAuthed: (value: boolean) => void;
}

const PaymentAuthContext = createContext<PaymentAuthContextType | undefined>(undefined);

const PAYMENT_AUTHED_KEY = 'paymentAuthed';

export const PaymentAuthProvider = ({children}: { children: ReactNode }) => {
    // sessionStorage에서 초기값 복원 — 모바일 BankPay form.submit() 후 페이지 새로고침 시 인증 유지
    const [isPaymentAuthed, setPaymentAuthedState] = useState(
        () => sessionStorage.getItem(PAYMENT_AUTHED_KEY) === 'true'
    );

    const setPaymentAuthed = useCallback((value: boolean) => {
        setPaymentAuthedState(value);
        if (value) {
            sessionStorage.setItem(PAYMENT_AUTHED_KEY, 'true');
        } else {
            sessionStorage.removeItem(PAYMENT_AUTHED_KEY);
        }
    }, []);

    return (
        <PaymentAuthContext.Provider value={{isPaymentAuthed, setPaymentAuthed}}>
            {children}
        </PaymentAuthContext.Provider>
    );
};
export const usePaymentAuth = () => {
    const context = useContext(PaymentAuthContext);
    if (!context) {
        throw new Error('usePaymentAuth는 PaymentAuthProvider 내부에서 사용해야 합니다');
    }
    return context;
};

/**
 * 결제 비밀번호 입력 필수 ProtectedRoute
 * 결제 비밀번호를 입력하지 않으면 /payment-password 페이지로 리다이렉트
 */
export const PaymentPasswordRoute = () => {
    const { isPaymentAuthed } = usePaymentAuth();
    const location = useLocation();

    if (!isPaymentAuthed) {
        return (
            <Navigate
                to="/payment-password"
                replace
                state={{
                    returnPath: location.pathname + location.search,
                }}
            />
        );
    }

    return <Outlet />;
};


export default ProtectedRoute;